import { Enum_Question_Type, toValidNumber } from '@cyren/common-lib';
import { memo, useEffect, useState, useRef, useLayoutEffect } from 'react';
import useInAdminDashboard from '../../admin/hooks/use-in-admin-dashboard';
import { TQuestion } from '../../graphql-enhanced';
import QsAbdLocation from '../../report/components/QsAbdLocation';
import QsFileInput from '../../report/components/QsFileInput';
import QsInputRange from '../../report/components/QsInputRange';
import { getAutocompletePropForQs } from '../../utils/models/question';
import { QuestionProps } from '../pa-prop-types';
import QsBloodPressure from '../question/typed-qs/QsBloodPressure';
import QsCollapsible from '../question/typed-qs/QsCollapsible';
import QsFrequencyInput from '../question/typed-qs/QsFrequencyInput';
import QsInputBodySelect from '../question/typed-qs/QsInputBodySelect';
import QsInputDate from '../question/typed-qs/QsInputDate';
import QsInputDateDob from '../question/typed-qs/QsInputDateDob';
import QsInputDatetime from '../question/typed-qs/QsInputDatetime';
import QsInputNumberUnit from '../question/typed-qs/QsInputNumberUnit';
import QsInputOnset from '../question/typed-qs/QsInputOnset';
import QsInputOnsetV2 from '../question/typed-qs/QsInputOnsetV2';
import QsInputRelativeOnset from '../question/typed-qs/QsInputRelativeOnset';
import QsInputSelect from '../question/typed-qs/QsInputSelect';
import QsInputSelectCountry from '../question/typed-qs/QsInputSelectCountry';
import QsInputSxCollector from '../question/typed-qs/QsInputSxCollector';
import QsInputSxIntelligentCollector from '../question/typed-qs/QsInputSxIntelligentCollector';
import QsInputSxSelector from '../question/typed-qs/QsInputSxSelector';
import QsInputTime from '../question/typed-qs/QsInputTime';
import QsTextInput from '../question/typed-qs/QsTextInput';
import QsTextarea from '../question/typed-qs/QsTextarea';
import { useQuestion } from './use-question';
import QsInputSignature from '../question/typed-qs/QsInputSignature';
import QsStageMileMarker from '../question/typed-qs/QsStageMileMarker';
import QsInputPhoneNumber from '../question/typed-qs/QsInputPhoneNumber';
import QsInputSxAssociatedCollector from '../question/typed-qs/QsInputSxAssociatedCollector';
import QsInputMultiText from '../question/typed-qs/QsInputMultiText';
import QsInputMultiTextNested from '../question/typed-qs/QsInputMultiTextNested';

function QuestionField(
  props: QuestionProps & {
    question: TQuestion;
  },
) {
  const {
    error,
    treeKey,
    locale,
    question,
    treeState,
    updateAnswer,
    updateValue,
    onUiMetadataChange,
    mode,
    patientDetails,
  } = props;

  const { inAdminPanel } = useInAdminDashboard();

  const {
    isBodySelectTypes,
    isNumberType,
    isRange,
    isSxCollectType,
    isSxSelectType,
    isSelectTypes,
    isNumberUnitSelect,
    isCollapsible,
    isMarkdown,
  } = useQuestion(question);

  const isDisplayMode = mode === 'display';
  const { type: typeRaw, questionKey, typeOptions } = question;

  const { answerMap } = treeState || {};

  const qAnswerValue = answerMap?.[questionKey] || {};
  const values = qAnswerValue?.answerValues || [];
  const firstValue = values?.[0];
  const secondValue = values?.[1];
  
    // 자동완성이 이미 완료된 질문들을 추적
  const [autoCompletedQuestions, setAutoCompletedQuestions] = useState<Set<string>>(new Set());

  // 환자 데이터를 기반으로 자동완성 값 생성
  const getAutoCompleteValue = () => {
    if (!patientDetails) return firstValue;
    // 환자 관련 질문들에 대해 자동으로 값 설정
    switch (questionKey) {
      case 'q-patient-first-name':
        return patientDetails.firstName || firstValue;
      case 'q-patient-last-name':
        return patientDetails.lastName || firstValue;
      case 'q-patient-birthday':
        return patientDetails.dob || firstValue;
      case 'q-patient-sex':
        return patientDetails.gender || firstValue;
      case 'q-patient-phone':
        return patientDetails.phoneNumber || firstValue;
      case 'q-patient-email':
        return patientDetails.email || firstValue;
      case 'q-patient-address':
        return patientDetails.address || firstValue;
      case 'q-patient-city':
        return patientDetails.city || firstValue;
      case 'q-patient-state':
        return patientDetails.state || firstValue;
      case 'q-patient-zip':
        return patientDetails.zipCode || firstValue;
      default:
        return firstValue;
    }
  };

  // select 타입을 위한 자동완성 answerKeys 생성
  const getAutoCompleteAnswerKeys = () => {
    if (!patientDetails) return qAnswerValue?.answerKeys || [];
    return qAnswerValue?.answerKeys || [];
    // switch (questionKey) {
    //   case 'q-patient-sex':
    //     if (patientDetails.sex) {
    //       // gender 값이 answerKeys 중 하나와 일치하는지 확인
    //       // key가 o-male 아니면 o-female
    //       // 그리고 우리가 가진 sex는 M/F 형태이므로, answerKeys에서 'male' 또는 'female'과 매칭
    //       // 네, o-male 또는 o-female을 반환해야 합니다.
    //       let sexKey;
    //       if (patientDetails.sex === 'M') {
    //         sexKey = question.answerKeys?.find(key => key === 'o-male');
    //         return ['M'];
    //       }
    //       if (patientDetails.sex === 'F') {
    //         sexKey = question.answerKeys?.find(key => key === 'o-female');
    //         return ['F'];
    //       }
    //       return [];
    //       break;
    //     }
    //   default:
    //     return qAnswerValue?.answerKeys || [];
    // }
  };

  const autoCompleteValue = getAutoCompleteValue();
  const autoCompleteAnswerKeys = getAutoCompleteAnswerKeys();

  // 성별 자동완성만 useLayoutEffect로 처리 (answerKeys 의존성 없이)
  useLayoutEffect(() => {
    if (questionKey === 'q-patient-sex' && patientDetails && updateAnswer && !autoCompletedQuestions.has(questionKey)) {
      
      if (patientDetails.sex || patientDetails.gender || patientDetails.sexCode) {
        const genderValue = patientDetails.sex || patientDetails.gender || patientDetails.sexCode;

        // answerKeys가 준비되지 않았으면 건너뜀
        if (!question.answerKeys || !Array.isArray(question.answerKeys) || question.answerKeys.length === 0) {
          return;
        }

        let matchingKey: string | undefined;

        // answerKeys 배열에서 'M' 또는 'F'와 정확히 일치하는 값을 우선 찾음
        if (genderValue === 'M' || genderValue === 'MALE') {
          matchingKey = question.answerKeys.find(key =>
            key === 'M' || key.toLowerCase() === 'm' || key.toLowerCase() === 'male'
          );
          // 없으면 'male'이 포함된 키를 찾음 (단, 'female'이 포함된 것은 제외)
          if (!matchingKey) {
            matchingKey = question.answerKeys.find(key =>
              key.toLowerCase().includes('male') && !key.toLowerCase().includes('female')
            );
          }
        } else if (genderValue === 'F' || genderValue === 'FEMALE') {
          matchingKey = question.answerKeys.find(key =>
            key === 'F' || key.toLowerCase() === 'f' || key.toLowerCase() === 'female'
          );
          // 없으면 'female'이 포함된 키를 찾음
          if (!matchingKey) {
            matchingKey = question.answerKeys.find(key =>
              key.toLowerCase().includes('female')
            );
          }
        }

        // 그래도 없으면 첫 번째 answerKey를 fallback으로 사용
        if (!matchingKey && question.answerKeys.length > 0) {
          [matchingKey] = question.answerKeys;
        }

        if (matchingKey && !firstValue) {
          updateAnswer({
            questionKey,
            answerKeys: [matchingKey],
          });
          
          // 자동완성이 완료된 질문으로 표시
          setAutoCompletedQuestions(prev => {
            const newSet = new Set(Array.from(prev).concat(questionKey));
            return newSet;
          });
        }
      }
    }
  }, [patientDetails, questionKey, firstValue, updateAnswer, autoCompletedQuestions]); // answerKeys 의존성 제거

  // 나머지 정보 자동완성은 useEffect로 처리
  useEffect(() => {
    // 성별은 이미 useLayoutEffect에서 처리했으므로 건너뜀
    if (questionKey === 'q-patient-sex') {
      return;
    }
    
    // 이미 자동완성이 완료된 질문이면 실행하지 않음
    if (autoCompletedQuestions.has(questionKey)) {
      return;
    }
    
    // patientDetails가 있고 updateAnswer가 있을 때만 실행
    if (patientDetails && updateAnswer) {
      let shouldUpdate = false;
      let newValue: string | undefined;

      switch (questionKey) {
        case 'q-patient-first-name':
          // 실제 필드명들을 모두 체크
          if ((patientDetails.firstName || patientDetails.first_name || patientDetails.firstname || 
               patientDetails.name || patientDetails.firstName || patientDetails.firstname) && !firstValue) {
            newValue = patientDetails.firstName || patientDetails.first_name || patientDetails.firstname || 
                      patientDetails.name || patientDetails.firstName || patientDetails.firstname;
            shouldUpdate = true;
          }
          break;
        case 'q-patient-last-name':
          if ((patientDetails.lastName || patientDetails.last_name || patientDetails.lastname || 
               patientDetails.name || patientDetails.lastName || patientDetails.lastname) && !firstValue) {
            newValue = patientDetails.lastName || patientDetails.last_name || patientDetails.lastname || 
                      patientDetails.name || patientDetails.lastName || patientDetails.lastname;
            shouldUpdate = true;
          }
          break;
        case 'q-patient-birthday':
          if ((patientDetails.dob || patientDetails.birthdate || patientDetails.birthDate || 
               patientDetails.dateOfBirth) && !firstValue) {
            newValue = patientDetails.dob || patientDetails.birthdate || patientDetails.birthDate || 
                      patientDetails.dateOfBirth;
            shouldUpdate = true;
          }
          break;
        case 'q-patient-phone':
          if ((patientDetails.phoneNumber || patientDetails.mobilephone || patientDetails.phone) && !firstValue) {
            newValue = patientDetails.phoneNumber || patientDetails.mobilephone || patientDetails.phone;
            shouldUpdate = true;
          }
          break;
        case 'q-patient-email':
          if ((patientDetails.email || patientDetails.emailaddress) && !firstValue) {
            newValue = patientDetails.email || patientDetails.emailaddress;
            shouldUpdate = true;
          }
          break;
        default:
          break;
      }

      if (shouldUpdate && newValue) {
        
        updateAnswer({
          questionKey,
          answerValues: [newValue],
        });
        
        // 자동완성이 완료된 질문으로 표시
        setAutoCompletedQuestions(prev => {
          const newSet = new Set(Array.from(prev).concat(questionKey));
          return newSet;
        });
      }
    }
  }, [patientDetails, questionKey, firstValue, updateAnswer, autoCompletedQuestions]); // answerKeys 의존성 제거

  if (isMarkdown || typeRaw == null) {
    return null;
  }

  const type: `${Enum_Question_Type}` = `${typeRaw}`;

  if (isBodySelectTypes) {
    return <QsInputBodySelect {...props} answerValue={qAnswerValue} />;
  }

  if (type === 'select_abd_location') {
    return (
      <QsAbdLocation question={question} answerValue={qAnswerValue} updateAnswer={updateAnswer} />
    );
  }

  if (isNumberUnitSelect) {
    return (
      <QsInputNumberUnit
        locale={locale}
        treeKey={treeKey}
        question={question}
        error={error}
        updateAnswer={updateAnswer}
        updateValue={updateValue}
        treeState={treeState}
      />
    );
  }

  if (isCollapsible) {
    return (
      <QsCollapsible
        locale={locale}
        treeKey={treeKey}
        question={question}
        value={firstValue || ''}
        error={error}
        updateAnswer={updateAnswer}
        updateValue={updateValue}
      />
    );
  }

  if (type === 'frequency') {
    return (
      <QsFrequencyInput
        value={[firstValue, secondValue]}
        onChange={(value) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: value,
          });
        }}
      />
    );
  }

  if (type === 'select_country') {
    return (
      <QsInputSelectCountry
        value={firstValue}
        onChange={(e) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerKeys: [e.target.value],
          });
        }}
      />
    );
  }

  if (type === 'select_countries') {
    return (
      <QsInputSelectCountry
        values={qAnswerValue?.answerKeys}
        multi
        onChangeValues={(nValues) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerKeys: nValues,
          });
        }}
      />
    );
  }

  if (type === 'textarea') {
    return (
      <QsTextarea
        locale={locale}
        error={error}
        question={question}
        max={toValidNumber(typeOptions?.max)}
        maxLength={toValidNumber(typeOptions?.max)}
        value={autoCompleteValue || ''}
        updateValue={updateValue}
        onUpdate={(nValue) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [`${nValue}`],
          });
        }}
      />
    );
  }

  if (type === 'image') {
    return (
      <QsFileInput
        value={firstValue || ''}
        onChange={(e) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [e.target.value],
          });
        }}
      />
    );
  }

  if (type === 'text') {
    // const restrictInput = ['firstName','lastName'].includes(question?.systemKey as string) 
    //   ? 'valid name' : null;
    return (
      <QsTextInput
        question={question}
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={autoCompleteValue || ''}
        autoComplete={getAutocompletePropForQs({ question })}
        restrictInput={null}
        onUpdate={(value) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [`${value}`],
          });
        }}
      />
    );
  }

  if (type === 'signature') {
    return (
      <QsInputSignature
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={autoCompleteValue || ''}
        autoComplete={getAutocompletePropForQs({ question })}
        mode={mode}
        onUpdate={(value) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [`${value}`],
          });
        }}
      />
    );
  }

  if (type === 'blood_pressure') {
    return (
      <QsBloodPressure
        values={values}
        onUpdate={(nValues) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: nValues,
          });
        }}
      />
    );
  }

  if (isNumberType) {
    return (
      <QsTextInput
        question={question}
        updateValue={updateValue}
        locale={locale}
        error={error}
        isDisplayMode={isDisplayMode}
        value={autoCompleteValue || ''}
        type='number'
        options={typeOptions}
        onUpdate={(value) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [`${value}`],
          });
        }}
      />
    );
  }

  if (isRange) {
    return (
      <QsInputRange
        value={firstValue || ''}
        options={typeOptions}
        onChange={(e) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [e.target.value],
          });
        }}
      />
    );
  }

  if (type === 'date_dob') {
    return (
      <QsInputDateDob
        question={question}
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={autoCompleteValue}
        onChange={(v) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [v],
          });
        }}
      />
    );
  }

  if (type === 'datetime_relative_onset') {
    if (!onUiMetadataChange) {
      if (inAdminPanel) {
        return null;
      }

      return (
        <div className='py-5'>
          <div className='text-error'>Property is missing for system (onUiMetadataChange)</div>
        </div>
      );
    }

    return (
      <QsInputRelativeOnset
        {...props}
        error={error}
        uiMetadata={qAnswerValue?.uiMetadata}
        onUiMetadataChange={onUiMetadataChange}
        value={autoCompleteValue}
        question={question}
        onChange={(v) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: v ? [v] : [],
          });
        }}
      />
    );
  }
  if (type === 'onset_v2') {
    return (
      <QsInputOnsetV2
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={firstValue}
        onChange={(v) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [v],
          });
        }}
      />
    );
  }

  if (type === 'onset') {
    return (
      <QsInputOnset
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={firstValue}
        onChange={(v) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [v],
          });
        }}
      />
    );
  }
  if (type === 'datetime') {
    return (
      <QsInputDatetime
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={firstValue}
        onChange={(v) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [v],
          });
        }}
      />
    );
  }

  if (type === 'date') {
    return (
      <QsInputDate
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={firstValue}
        onChange={(v) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [v],
          });
        }}
        mode={mode}
      />
    );
  }
  if (type === 'time') {
    return (
      <QsInputTime
        value={firstValue}
        onChange={(v) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [v],
          });
        }}
      />
    );
  }

  if (type === 'stage_milemarker') {
    return (
      <QsStageMileMarker 
        {...props}
      />
    );
  }



  if (type === 'phone_number') {
    return (<QsInputPhoneNumber
      value={values}
      locale={locale}
      updateValue={() => { }}
      onUpdate={(value) => {
        if (!updateAnswer) return;
        updateAnswer({
          questionKey,
          answerValues: [`${value}`],
        });
      }}
      typeOptions={typeOptions}
    />
    )
  }

  if (isSelectTypes) {
    // select 타입에서도 patientDetails를 전달
    return <QsInputSelect {...props} patientDetails={patientDetails} />;
  }

  if (isSxSelectType) {
    return <QsInputSxSelector {...props} />;
  }

  if (isSxCollectType) {
    return <QsInputSxCollector {...props} />;
  }

  if (type === "sx_collect_intelligent") {
    return <QsInputSxIntelligentCollector {...props} />;
  }

  if (type === "sx_collect_associated") {
    return <QsInputSxAssociatedCollector {...props} />;
  }

  if (type === 'multi_text') {
    return (
      <QsInputMultiText
        question={question}
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={autoCompleteValue || ''}
        options={typeOptions}
        onUpdate={(value) => {
          if (!updateAnswer) return;
          updateAnswer({
            questionKey,
            answerValues: [`${value}`],
          });
        }}
      />
    );
  }

  if (type === 'multi_text_nested') {
    return (
      <QsInputMultiTextNested
        question={question}
        updateValue={updateValue}
        locale={locale}
        error={error}
        value={autoCompleteValue || ''}
        options={typeOptions}
        answerMaps={qAnswerValue?.answerMaps as Array<{
          answerKeys: string[];
          answerLabels: string[];
          notesForKeys?: Record<string, string>;
        }> | undefined}
        nestedQuestions={question.answerOptionsMap ? (() => {
          // answerOptionsMap을 nestedQuestions 구조로 변환
          const optionsByQuestion: Record<string, Array<{key: string, label: string, requireNote?: boolean, mutuallyExclusive?: boolean}>> = {};
          
          Object.entries(question.answerOptionsMap as Record<string, any>).forEach(([key, option]) => {
            const questionText = option?.question || 'Nested Question 1';
            if (!optionsByQuestion[questionText]) {
              optionsByQuestion[questionText] = [];
            }
            optionsByQuestion[questionText].push({
              key,
              label: option?.label || key,
              requireNote: option?.requireNote || false,
              mutuallyExclusive: option?.mutuallyExclusive || false
            });
          });
          
          // 각 질문별로 정렬 (order 필드 사용)
          Object.keys(optionsByQuestion).forEach(questionText => {
            optionsByQuestion[questionText].sort((a, b) => {
              const aOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.label === a.label);
              const bOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.label === b.label);
              const aOrder = aOption?.order || 0;
              const bOrder = bOption?.order || 0;
              return aOrder - bOrder;
            });
          });
          
          // 질문들을 questionIndex 순서로 정렬
          const sortedQuestions = Object.entries(optionsByQuestion).sort(([aText, aOptions], [bText, bOptions]) => {
            const aFirstOption = aOptions[0];
            const bFirstOption = bOptions[0];
            const aOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.label === aFirstOption?.label);
            const bOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.label === bFirstOption?.label);
            const aQuestionIndex = aOption?.questionIndex || 0;
            const bQuestionIndex = bOption?.questionIndex || 0;
            return aQuestionIndex - bQuestionIndex;
          });
          
          return sortedQuestions.map(([questionText, options]) => ({
            question: questionText,
            options
          }));
        })() : []}
        onUpdate={(value, answerMaps) => {
          if (!updateAnswer) return;
          
          console.log('QuestionField multi_text_nested onUpdate', {
            value,
            answerMaps
          });
          
          updateAnswer({
            questionKey,
            answerValues: [`${value}`],
            answerMaps: answerMaps && answerMaps.length > 0 ? answerMaps : undefined
          });
        }}
      />
    );
  }

  // TODO log this render cases
  return <div className='text-error'>Error: Type not supported ({type})</div>;
}

export default memo(QuestionField);
